<template>
  <div class="feedback-wrapper">
    <!-- 浮动按钮 -->
    <el-tooltip content="意见反馈" placement="left" :show-after="300">
      <button class="feedback-float-btn" @click="showDialog = true">
        <el-icon><ChatDotRound /></el-icon>
        <span class="btn-text">反馈</span>
      </button>
    </el-tooltip>

    <!-- 反馈弹窗 -->
    <el-dialog
      v-model="showDialog"
      title="意见反馈"
      width="500px"
      :close-on-click-modal="false"
      :lock-scroll="false"
      class="feedback-dialog"
    >
      <div class="feedback-content">
        <p class="feedback-tip">感谢您帮助我们改进产品！</p>

        <el-form
          ref="formRef"
          :model="form"
          :rules="rules"
          label-position="top"
        >
          <el-form-item label="反馈类型" prop="type">
            <el-radio-group v-model="form.type">
              <el-radio :value="1">BUG反馈</el-radio>
              <el-radio :value="2">功能建议</el-radio>
              <el-radio :value="3">内容纠错</el-radio>
              <el-radio :value="4">其他</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="标题" prop="title">
            <el-input
              v-model="form.title"
              placeholder="请简要描述您的问题或建议"
              maxlength="100"
              show-word-limit
            />
          </el-form-item>

          <el-form-item label="详细描述" prop="content">
            <el-input
              v-model="form.content"
              type="textarea"
              :rows="4"
              placeholder="请详细描述您遇到的问题或建议..."
              maxlength="1000"
              show-word-limit
            />
          </el-form-item>

          <el-form-item v-if="!userStore.isLoggedIn" label="联系方式" prop="contact">
            <el-input
              v-model="form.contact"
              placeholder="请留下您的邮箱或手机号，方便我们回复"
            />
          </el-form-item>

          <el-form-item label="上传截图（选填，最多5张）">
            <div class="screenshot-upload-list">
              <div
                v-for="(url, index) in form.screenshots"
                :key="index"
                class="screenshot-item"
              >
                <img :src="url" class="screenshot-image" />
                <div class="screenshot-actions">
                  <el-icon class="action-icon" @click="previewImage(url)"><View /></el-icon>
                  <el-icon class="action-icon delete" @click="removeImage(index)"><Delete /></el-icon>
                </div>
              </div>
              <div
                v-if="form.screenshots.length < 5"
                class="screenshot-uploader"
              >
                <el-upload
                  action="#"
                  :before-upload="handleBeforeUpload"
                  accept="image/*"
                  :show-file-list="false"
                  :disabled="uploading"
                  class="uploader-inner"
                >
                  <div v-if="uploading" class="upload-progress">
                    <el-progress
                      :percentage="uploadProgress"
                      :stroke-width="6"
                      :show-text="false"
                      style="width: 60px"
                    />
                    <span class="uploading-text">上传中</span>
                  </div>
                  <div v-else class="upload-placeholder">
                    <el-icon :size="24"><Plus /></el-icon>
                  </div>
                </el-upload>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">
          提交反馈
        </el-button>
      </template>
    </el-dialog>

    <!-- 图片预览 -->
    <el-image-viewer
      v-if="previewVisible"
      :url-list="previewList"
      :initial-index="previewIndex"
      @close="previewVisible = false"
    />

    <!-- 提交成功提示 -->
    <el-dialog
      v-model="showSuccess"
      title="提交成功"
      width="400px"
      :show-close="false"
      class="success-dialog"
    >
      <div class="success-content">
        <div class="success-icon">✅</div>
        <h3>感谢您的反馈！</h3>
        <p>我们会尽快处理您的反馈，并通过邮件或短信回复您。</p>
        <el-button type="primary" @click="showSuccess = false">知道了</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { ChatDotRound, View, Delete, Plus } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'
import { submitFeedback, type SubmitFeedbackRequest } from '@/api/feedback'
import { uploadFile } from '@/api/upload'

const userStore = useUserStore()

// 弹窗显示状态
const showDialog = ref(false)
const showSuccess = ref(false)
const submitting = ref(false)

// 图片上传
const uploading = ref(false)
const uploadProgress = ref(0)

// 图片预览
const previewVisible = ref(false)
const previewIndex = ref(0)

// 表单
const formRef = ref()
const form = reactive<SubmitFeedbackRequest>({
  type: 1,
  title: '',
  content: '',
  contact: '',
  screenshots: []
})

// 预览列表
const previewList = computed(() => form.screenshots || [])

// 表单验证规则
const rules = {
  type: [{ required: true, message: '请选择反馈类型', trigger: 'change' }],
  title: [
    { required: true, message: '请输入标题', trigger: 'blur' },
    { min: 5, max: 100, message: '标题长度在5-100个字符之间', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入详细描述', trigger: 'blur' },
    { min: 10, max: 1000, message: '描述长度在10-1000个字符之间', trigger: 'blur' }
  ],
  contact: [
    { required: !userStore.isLoggedIn, message: '请输入联系方式', trigger: 'blur' }
  ]
}

// 处理图片上传
const handleBeforeUpload = async (file: File) => {
  // 检查文件类型
  const isImage = file.type.startsWith('image/')
  if (!isImage) {
    ElMessage.error('请上传图片文件')
    return false
  }

  // 检查文件大小 (5MB)
  const maxSize = 5 * 1024 * 1024
  if (file.size > maxSize) {
    ElMessage.error('图片大小不能超过 5MB')
    return false
  }

  // 检查图片数量
  if (form.screenshots.length >= 5) {
    ElMessage.warning('最多只能上传 5 张图片')
    return false
  }

  uploading.value = true
  uploadProgress.value = 0

  try {
    const { url } = await uploadFile(file, {
      business_type: 'feedback',
      is_public: true,
      onProgress: (progress) => {
        uploadProgress.value = progress
      }
    })

    form.screenshots.push(url)
    ElMessage.success('上传成功')
  } catch (error: any) {
    ElMessage.error(error.message || '上传失败')
  } finally {
    uploading.value = false
    uploadProgress.value = 0
  }

  return false
}

// 预览图片
const previewImage = (url: string) => {
  const index = form.screenshots.indexOf(url)
  if (index !== -1) {
    previewIndex.value = index
    previewVisible.value = true
  }
}

// 移除图片
const removeImage = (index: number) => {
  form.screenshots.splice(index, 1)
}

// 提交反馈
const handleSubmit = async () => {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return

  // 未登录用户必须填写联系方式
  if (!userStore.isLoggedIn && !form.contact) {
    ElMessage.warning('请填写联系方式')
    return
  }

  submitting.value = true
  try {
    await submitFeedback(form)
    showDialog.value = false
    showSuccess.value = true

    // 重置表单
    formRef.value?.resetFields()
    form.screenshots = []
  } catch (error) {
    console.error('提交反馈失败:', error)
    ElMessage.error('提交失败，请稍后重试')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped lang="scss">
.feedback-wrapper {
  position: fixed;
  right: 24px;
  bottom: 100px;
  z-index: 100;
}

.feedback-float-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(var(--primary-rgb), 0.5);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  .el-icon {
    font-size: 20px;
    margin-bottom: 2px;
  }

  .btn-text {
    font-size: 11px;
    font-weight: 500;
  }
}

.feedback-content {
  .feedback-tip {
    color: var(--text-secondary);
    font-size: 14px;
    margin: 0 0 var(--spacing-md);
    padding-bottom: var(--spacing-md);
    border-bottom: 1px solid var(--border-color);
  }
}

.screenshot-upload-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  .screenshot-item {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--border-color);

    .screenshot-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .screenshot-actions {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      opacity: 0;
      transition: opacity 0.3s;

      .action-icon {
        color: white;
        font-size: 20px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: background 0.3s;

        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        &.delete:hover {
          background: rgba(245, 108, 108, 0.8);
        }
      }
    }

    &:hover .screenshot-actions {
      opacity: 1;
    }
  }

  .screenshot-uploader {
    width: 100px;
    height: 100px;
    flex-shrink: 0;

    .uploader-inner {
      width: 100px;
      height: 100px;

      :deep(.el-upload) {
        width: 100px;
        height: 100px;
      }

      :deep(.el-upload-dragger) {
        width: 100px;
        height: 100px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px dashed var(--border-color);
        border-radius: 8px;
        background: var(--bg-secondary);

        &:hover {
          border-color: var(--primary-color);
          background: var(--primary-light);
        }

        .upload-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;

          .el-icon {
            font-size: 24px;
            color: var(--text-secondary);
          }
        }

        .upload-progress {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;

          .uploading-text {
            font-size: 10px;
            color: var(--text-secondary);
            margin-top: 4px;
          }
        }
      }
    }
  }
}

.success-content {
  text-align: center;
  padding: var(--spacing-lg);

  .success-icon {
    font-size: 64px;
    margin-bottom: var(--spacing-md);
  }

  h3 {
    font-size: 18px;
    color: var(--text-primary);
    margin: 0 0 var(--spacing-sm);
  }

  p {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 var(--spacing-lg);
    line-height: 1.6;
  }
}
</style>
